import {
  Typography,
  TYPOGRAPHY_WEIGHT
} from '../../../../components/Typography'
import {
  ILedByGPORelations,
  ILedByGPOTable
} from '../../../../redux/store/adminSplitFees/types'
import { columns } from './constants'
import { Table } from '../../../../components/Table'
import { useCallback, useEffect, useState } from 'react'
import './styles.scss'
import { useSelector } from 'react-redux'
import { getUser } from '../../../../redux/store/user/getters'
import {
  ACTIONS,
  contractSubject,
  defineAbility,
  SUBJECTS
} from '../../Abilities'
import { BTN_TXT } from '../../../../constants'
import { Button, BUTTON_TYPES } from '../../../../components/Button'
import { ReactComponent as Edit16 } from '../../../../assets/svg/Edit16.svg'
import { useSplitFeesPopup } from '../../SplitFees/Providers/SplitFeesPopupProvider'

type TProps = {
  relations: ILedByGPORelations[]
}

export const LedByGPOTable = ({ relations }: TProps) => {
  const [list, setList] = useState<ILedByGPOTable[]>([])
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [selectedRows, setSelectedRows] = useState<ILedByGPOTable[]>([])

  const {
    editLedByGPOPopup: { actions }
  } = useSplitFeesPopup()

  const user = useSelector(getUser)
  const ability = defineAbility(user)
  const canEditLedByGPOTable = ability.can(
    ACTIONS.EDIT,
    SUBJECTS.ADMIN_SPLIT_FEES
  )
  const canSeeCommunitySplit = ability.can(
    ACTIONS.VIEW,
    contractSubject(SUBJECTS.GPO_COMMUNITY_DATA, user)
  )

  const handleSelectRow = useCallback((selectedRowKeys, selectedRow) => {
    setSelectedRowKeys(selectedRowKeys)
    setSelectedRows(selectedRow)
  }, [])

  useEffect(() => {
    if (relations.length) {
      const listWithAdditionalRows: ILedByGPOTable[] = []
      relations.forEach(
        ({ record, health_system, is_shared_with_community }) => {
          const {
            remaining_to_be_distributed,
            gpo_retain,
            community_split,
            hs_split,
            uuid
          } = record
          const { name, community } = health_system
          const formattedGPORetain = Number(gpo_retain)

          listWithAdditionalRows.push({
            key: `${uuid}`,
            health_system_name:
              canSeeCommunitySplit && is_shared_with_community
                ? `${community.name} & ${name}`
                : name,
            gpo_retain,
            remaining_to_be_distributed:
              formattedGPORetain === 100 ? null : remaining_to_be_distributed,
            second_row: false,
            hs_split,
            community_split,
            not_completed: canEditLedByGPOTable
              ? canSeeCommunitySplit && is_shared_with_community
                ? Boolean(!gpo_retain && !community_split && !hs_split)
                : Boolean(!gpo_retain)
              : false,
            is_shared_with_community
          })
          if (formattedGPORetain !== 100)
            listWithAdditionalRows.push({
              key: `${uuid}_2`,
              health_system_name: null,
              hs_split,
              community_split,
              gpo_retain: null,
              remaining_to_be_distributed: null,
              second_row: true,
              not_completed: false,
              is_shared_with_community
            })
        }
      )
      setList(listWithAdditionalRows)
      onCancelSelection()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [relations, canSeeCommunitySplit])

  const onCancelSelection = useCallback(() => {
    setSelectedRowKeys([])
    setSelectedRows([])
  }, [])

  const onEditFees = useCallback(() => {
    actions.open({ selectedRow: selectedRows[0] })
  }, [actions, selectedRows])

  return (
    <div className="column gap-16 p-24">
      <Typography.Headline6>Admin split fees</Typography.Headline6>
      <div className="table-wrapper__header">
        {!!selectedRows.length ? (
          <Typography.Body1
            className="table-wrapper__select-title"
            weight={TYPOGRAPHY_WEIGHT.SEMI_BOLD}
          >
            Selected {selectedRowKeys.length} of {relations.length}
          </Typography.Body1>
        ) : null}
        {selectedRows.length > 0 && (
          <div className="row gap-8">
            <Button
              type={BUTTON_TYPES.DEFAULT}
              upperCase
              onClick={onCancelSelection}
            >
              {BTN_TXT.CANCEL}
            </Button>
            <Button
              type={BUTTON_TYPES.PRIMARY}
              upperCase
              icon={<Edit16 />}
              disabled={selectedRows.length !== 1}
              onClick={onEditFees}
            >
              {BTN_TXT.EDIT_FEES}
            </Button>
          </div>
        )}
      </div>
      <Table<ILedByGPOTable>
        dataSource={list}
        columns={columns(canSeeCommunitySplit)}
        className="gpo-table"
        rowKey={'key'}
        rowClassName={(record) =>
          record.not_completed ? 'warning-highlight' : ''
        }
        rowSelection={
          canEditLedByGPOTable
            ? {
                selectedRowKeys,
                onChange: handleSelectRow,
                hideSelectAll: true,
                getCheckboxProps: ({ second_row }) => {
                  return {
                    disabled: second_row
                  }
                }
              }
            : undefined
        }
      />
    </div>
  )
}
